import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useCheckoutContext, Article } from '@zooplus/checkout-state';

import CartEmpty from '@components/cart/cart-empty';
import FeedbackMessage from '@components/common/notifications/feedback-message';
import OrderSummary from '@components/common/order-summary';
import PageCommonLayout from '@components/common/page-common-layout';
import PageLoader from '@components/common/page-loader';
import PaymentIncompleteModal from '@components/preview/payment-incomplete-modal';
import PreviewContainersSection from '@components/preview/preview-containers-section';
import { useMobileBasketCount } from '@hooks/use-mobile-basket-count';
import { usePreventPageUnload } from '@hooks/use-prevent-page-unload';
import { useTranslation } from '@hooks/use-translations';
import {
  CHECKOUT_STATE_PROJECT_ID,
  MESSAGE_PLACEMENT,
  CUSTOMER_TYPE,
  PAGE_TYPE,
  PAGE_PATH,
} from '@utilities/constants';
import { isArrayNotEmpty, redirectToUrl } from '@utilities/utils';

const Preview = () => {
  const {
    checkoutState,
    isFetching,
    mainTogglesMap = {},
    setPageType,
    isEditStepTriggered,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  usePreventPageUnload(isEditStepTriggered);

  const articles = checkoutState?.cart?.articles;
  const customerType = checkoutState?.customer?.customerType;
  const isNewPreviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_PREVIEW_PAGE;
  useMobileBasketCount(articles || []);

  const isExistingOrNewCustomer =
    customerType &&
    [CUSTOMER_TYPE.EXISTING_CUSTOMER, CUSTOMER_TYPE.NEW_CUSTOMER].includes(customerType);
  const isGuestCustomer = customerType === CUSTOMER_TYPE.GUEST_CUSTOMER;

  const shouldShowNewPreviewPage =
    (!isFetching && isArrayNotEmpty(articles as Article[]) && isNewPreviewPage) ||
    pathname?.includes(PAGE_PATH.GUEST_PREVIEW);

  useEffect(() => {
    setPageType?.(PAGE_TYPE.PREVIEW);
  }, [setPageType]);

  useEffect(() => {
    if (!isFetching && !isNewPreviewPage && pathname?.includes(PAGE_PATH.NEW_PREVIEW)) {
      redirectToUrl(PAGE_PATH.PREVIEW);
    }
  }, [isFetching, isNewPreviewPage, pathname]);

  useEffect(() => {
    if (!isFetching) {
      if (pathname?.includes(PAGE_PATH.GUEST_PREVIEW) && isExistingOrNewCustomer) {
        redirectToUrl(PAGE_PATH.NEW_PREVIEW);
      }

      if (pathname?.includes(PAGE_PATH.NEW_PREVIEW) && isGuestCustomer) {
        redirectToUrl(PAGE_PATH.GUEST_PREVIEW);
      }
    }
  }, [isFetching, pathname, isExistingOrNewCustomer, isGuestCustomer]);

  if (isFetching) {
    return (
      <>
        <PageLoader />
      </>
    );
  }

  if (shouldShowNewPreviewPage) {
    return (
      <>
        <PageCommonLayout
          pageHeading={t('checkout.preview.heading')}
          leftColumnContent={<PreviewContainersSection />}
          rightColumnContent={<OrderSummary />}
          feedbackMessage={<FeedbackMessage placement={MESSAGE_PLACEMENT.PREVIEW_BOTTOM_RIGHT} />}
        />
        <PaymentIncompleteModal />
      </>
    );
  }
  return <CartEmpty />;
};

export default Preview;
